import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Modal,
    ModalVariant,
    Button,
    Checkbox,
    Alert,
    TextContent,
    Text,
    TextVariants,
    Tooltip,
    TreeView,
    TreeViewDataItem,
} from '@patternfly/react-core';
import { ExclamationTriangleIcon } from '@patternfly/react-icons';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { ContouringRoi, ContouringCustomizationOutput } from '../../../store/contouring/contouring-types';
import { StoreState } from '../../../store/store';
import { advancedRoisCopiedToOutputs, contouringSelectors } from '../../../store/contouring/contouringSlice';
import { selectContourTreeViewItems } from '../../../store/contouring/contouringSelectors';
import { showCustomizationTreeViewDataItem, BODY_PART_DISPLAY_ORDER } from '../../../store/global-types/customization-helpers';
import { filterCustomizationTreeByFreeText } from '../../../store/global-types/customizationSelectors';
import { getBodyPartIcon } from '../../../components/body-part-icon';
import { CustomizationTreeViewDataItem, ModelType } from '../../../store/global-types/customization-types';
import { getVisibilityStates } from '../../../store/global-types/filter-helpers';
import CustomizationFilters from '../CustomizationFilters';
import CustomizationSearchBar from '../CustomizationSearchBar';

interface CopyAdvancedRoisModalProps {
    isOpen: boolean;
    onClose: () => void;
    triggeringRoiId: string;
    sourceOutputId: string;
}

// Collect all leaf output IDs from a TreeViewDataItem subtree
const collectOutputIds = (node: TreeViewDataItem): string[] => {
    if (!node.children || node.children.length === 0) { return node.id ? [node.id] : []; }
    return node.children.flatMap(collectOutputIds);
};

// Get check state: true (all checked), false (none), null (indeterminate)
const getCheckState = (outputNodes: TreeViewDataItem[], selected: Set<string>): boolean | null => {
    const total = outputNodes.length;
    const checked = outputNodes.filter(o => o.id && selected.has(o.id)).length;
    if (checked === 0) { return false; }
    if (checked === total) { return true; }
    return null;
};

const getCheckStateFromChildren = (children: TreeViewDataItem[], selected: Set<string>, excludeId?: string): boolean | null => {
    const allIds = children.flatMap(collectOutputIds).filter(id => id !== excludeId);
    if (allIds.length === 0) { return false; }
    const checked = allIds.filter(id => selected.has(id)).length;
    if (checked === 0) { return false; }
    if (checked === allIds.length) { return true; }
    return null;
};

/** Returns names of enabled ROIs in the target output that match selected source ROI names. */
const getConflictingNames = (
    outputId: string | undefined,
    outputEntities: Record<string, ContouringCustomizationOutput | undefined>,
    roiEntities: Record<string, ContouringRoi | undefined>,
    selectedEnabledSourceNames: Set<string>,
): string[] => {
    if (!outputId) { return []; }
    const targetOutput = outputEntities[outputId];
    return [...new Set(
        (targetOutput?.rois ?? [])
            .map(rId => roiEntities[rId])
            .filter((r): r is ContouringRoi => r !== undefined && r.isIncluded)
            .map(r => r.name)
            .filter(n => selectedEnabledSourceNames.has(n))
    )];
};

/** Renders output/base node label with source styling, duplicate warning icon, or plain. */
const OutputNodeLabel = ({ label, isSource, conflictingNames, t }: {
    label: React.ReactNode;
    isSource: boolean;
    conflictingNames: string[];
    t: TFunction;
}) => {
    if (isSource) {
        return (<span className="copy-source-label">{label} <small>{t('copyAdvancedRois.modal.sourceLabel')}</small></span>);
    }
    if (conflictingNames.length > 0) {
        return (
            <span>
                {label}
                <Tooltip content={t('copyAdvancedRois.modal.duplicateNameWarning', { names: conflictingNames.join(', ') })}>
                    <ExclamationTriangleIcon className="copy-duplicate-warning-icon" />
                </Tooltip>
            </span>
        );
    }
    return <>{label}</>;
};

/** Renders a parent node (body-part/model/base) label with an optional bubble-up warning icon. */
const ParentNodeLabel = ({ label, hasDescendantConflicts, t }: {
    label: React.ReactNode;
    hasDescendantConflicts: boolean;
    t: TFunction;
}) => {
    if (hasDescendantConflicts) {
        return (
            <span>
                {label}
                <Tooltip content={t('copyAdvancedRois.modal.duplicateNameWarningGeneric')}>
                    <ExclamationTriangleIcon className="copy-duplicate-warning-icon" />
                </Tooltip>
            </span>
        );
    }
    return <>{label}</>;
};

interface BuildCopyTargetTreeParams {
    contourTreeViewItems: CustomizationTreeViewDataItem[];
    searchText: string;
    areDeprecatedModelsShown: boolean;
    areNonLicensedModelsShown: boolean;
    areUserHiddenModelsShown: boolean;
    sourceOutputId: string;
    selectedTargetOutputIds: Set<string>;
    sourceBaseId: string | undefined;
    sourceModelId: string | undefined;
    sourceBodyPart: string | undefined;
    selectedEnabledSourceNames: Set<string>;
    outputEntities: Record<string, ContouringCustomizationOutput | undefined>;
    roiEntities: Record<string, ContouringRoi | undefined>;
    t: TFunction;
}

/** Builds the tree data for the copy target selection tree. */
function buildCopyTargetTree(params: BuildCopyTargetTreeParams): TreeViewDataItem[] {
    const {
        contourTreeViewItems, searchText,
        areDeprecatedModelsShown, areNonLicensedModelsShown, areUserHiddenModelsShown,
        sourceOutputId, selectedTargetOutputIds,
        sourceBaseId, sourceModelId, sourceBodyPart,
        selectedEnabledSourceNames, outputEntities, roiEntities, t,
    } = params;

    // Apply search filter first (reuse existing sidebar filter)
    const filtered = filterCustomizationTreeByFreeText(contourTreeViewItems, searchText);

    const bodyPartNodes: TreeViewDataItem[] = [];

    for (const bpItem of filtered) {
        if (!showCustomizationTreeViewDataItem(bpItem, areNonLicensedModelsShown, areDeprecatedModelsShown, areUserHiddenModelsShown)) { continue; }

        const modelNodes: TreeViewDataItem[] = [];
        let bpHasConflicts = false;
        for (const modelItem of bpItem.children) {
            if (!showCustomizationTreeViewDataItem(modelItem, areNonLicensedModelsShown, areDeprecatedModelsShown, areUserHiddenModelsShown)) { continue; }

            const baseNodes: TreeViewDataItem[] = [];
            let modelHasConflicts = false;
            for (const baseItem of modelItem.children) {
                if (!showCustomizationTreeViewDataItem(baseItem, areNonLicensedModelsShown, areDeprecatedModelsShown, areUserHiddenModelsShown)) { continue; }

                const outputNodes: TreeViewDataItem[] = [];
                let baseHasConflicts = false;
                for (const outputItem of baseItem.children) {
                    if (!showCustomizationTreeViewDataItem(outputItem, areNonLicensedModelsShown, areDeprecatedModelsShown, areUserHiddenModelsShown)) { continue; }

                    const isSource = outputItem.id === sourceOutputId;
                    const conflictingNames = isSource ? [] : getConflictingNames(outputItem.id, outputEntities, roiEntities, selectedEnabledSourceNames);

                    if (conflictingNames.length > 0) {
                        baseHasConflicts = true;
                    }

                    outputNodes.push({
                        id: outputItem.id,
                        name: <OutputNodeLabel label={outputItem.label} isSource={isSource} conflictingNames={conflictingNames} t={t} />,
                        hasCheckbox: !isSource,
                        checkProps: isSource ? undefined : {
                            checked: selectedTargetOutputIds.has(outputItem.id),
                            ...({ 'data-testid': `copy-advanced-rois-target-checkbox-${outputItem.id}` } as Record<string, string>),
                        },
                    });
                }

                if (outputNodes.length === 0) { continue; }

                if (outputNodes.length === 1) {
                    // Collapse single output into the base node
                    const singleOutput = outputNodes[0];
                    const isSourceOnly = singleOutput.id === sourceOutputId;
                    const conflictingNames = isSourceOnly ? [] : getConflictingNames(singleOutput.id, outputEntities, roiEntities, selectedEnabledSourceNames);

                    baseNodes.push({
                        id: singleOutput.id,
                        name: <OutputNodeLabel label={baseItem.label} isSource={isSourceOnly} conflictingNames={conflictingNames} t={t} />,
                        hasCheckbox: !isSourceOnly,
                        checkProps: isSourceOnly ? undefined : { ...singleOutput.checkProps },
                        defaultExpanded: baseItem.id === sourceBaseId,
                    });
                } else {
                    // Multiple outputs: show full hierarchy
                    const selectableOutputNodes = outputNodes.filter(o => o.id !== sourceOutputId);
                    const baseChecked = getCheckState(selectableOutputNodes, selectedTargetOutputIds);
                    baseNodes.push({
                        id: `base-${baseItem.id}`,
                        name: <ParentNodeLabel label={baseItem.label} hasDescendantConflicts={baseHasConflicts} t={t} />,
                        checkProps: { checked: baseChecked },
                        children: outputNodes,
                        defaultExpanded: baseItem.id === sourceBaseId,
                    });
                }

                if (baseHasConflicts) {
                    modelHasConflicts = true;
                }
            }

            if (baseNodes.length === 0) { continue; }
            const allBasesSourceOnly = baseNodes.every(n => n.hasCheckbox === false);
            const modelChecked = allBasesSourceOnly
                ? undefined
                : getCheckStateFromChildren(baseNodes, selectedTargetOutputIds, sourceOutputId);
            modelNodes.push({
                id: `model-${modelItem.id}`,
                name: allBasesSourceOnly
                    ? <span className="copy-source-label">{modelItem.label}</span>
                    : <ParentNodeLabel label={modelItem.label} hasDescendantConflicts={modelHasConflicts} t={t} />,
                hasCheckbox: !allBasesSourceOnly,
                checkProps: allBasesSourceOnly ? undefined : { checked: modelChecked },
                children: baseNodes,
                defaultExpanded: modelItem.id === sourceModelId,
            });

            if (modelHasConflicts) {
                bpHasConflicts = true;
            }
        }

        if (modelNodes.length === 0) { continue; }
        const allModelsSourceOnly = modelNodes.every(n => n.hasCheckbox === false);
        const bpChecked = allModelsSourceOnly
            ? undefined
            : getCheckStateFromChildren(modelNodes, selectedTargetOutputIds, sourceOutputId);
        const bpDisplayInfo = BODY_PART_DISPLAY_ORDER.find(bp => bp.id === bpItem.id);
        const bpDisplayName = bpDisplayInfo ? t(bpDisplayInfo.translationKey) : bpItem.label;
        bodyPartNodes.push({
            id: `bp-${bpItem.id}`,
            name: allModelsSourceOnly
                ? <span className="copy-source-label">{bpDisplayName}</span>
                : <ParentNodeLabel label={bpDisplayName} hasDescendantConflicts={bpHasConflicts} t={t} />,
            icon: getBodyPartIcon(bpItem.id),
            hasCheckbox: !allModelsSourceOnly,
            checkProps: allModelsSourceOnly ? undefined : { checked: bpChecked },
            children: modelNodes,
            defaultExpanded: bpItem.label === sourceBodyPart,
        });
    }

    return bodyPartNodes;
}

const CopyAdvancedRoisModal = (props: CopyAdvancedRoisModalProps) => {
    const { isOpen, onClose, triggeringRoiId, sourceOutputId } = props;

    const dispatch = useDispatch();
    const { t } = useTranslation();

    // Get source output to find advanced ROIs
    const sourceOutput = useSelector((state: StoreState) => contouringSelectors.selectOutputById(state, sourceOutputId));

    // Get advanced (non-built-in) ROIs from the source output
    const advancedRois = useSelector((state: StoreState) => {
        if (!sourceOutput) { return [] as ContouringRoi[]; }
        return sourceOutput.rois
            .map(roiId => contouringSelectors.selectRoiById(state, roiId))
            .filter((roi): roi is ContouringRoi => roi !== undefined && !roi.isBuiltInRoi);
    });

    // Look up source output's ancestry for auto-expanding
    const sourceBase = useSelector((state: StoreState) => {
        if (!sourceOutput) { return undefined; }
        return contouringSelectors.selectCustomizationBaseById(state, sourceOutput.modelCustomizationBaseId);
    });
    const sourceModelId = sourceBase?.modelId;
    const sourceBodyPart = useSelector((state: StoreState) => {
        if (!sourceModelId) { return undefined; }
        const model = contouringSelectors.selectModelById(state, sourceModelId);
        return model?.bodyPart;
    });

    // Entity lookups for duplicate name warning
    const roiEntities = useSelector((state: StoreState) => contouringSelectors.selectRoiEntities(state));
    const outputEntities = useSelector((state: StoreState) => contouringSelectors.selectOutputEntities(state));

    // Tree data from existing selector
    const contourTreeViewItems = useSelector(selectContourTreeViewItems);
    const { areDeprecatedModelsShown, areNonLicensedModelsShown, areUserHiddenModelsShown } = useSelector(
        (state: StoreState) => getVisibilityStates(state, ModelType.Contouring)
    );

    // State
    const [selectedRoiIds, setSelectedRoiIds] = useState<Set<string>>(() => new Set([triggeringRoiId]));
    const [selectedTargetOutputIds, setSelectedTargetOutputIds] = useState<Set<string>>(new Set());
    const [searchText, setSearchText] = useState('');

    const handleRoiCheckboxChange = useCallback((roiId: string, checked: boolean) => {
        setSelectedRoiIds(prev => {
            const next = new Set(prev);
            if (checked) { next.add(roiId); } else { next.delete(roiId); }
            return next;
        });
    }, []);

    // Build tree data with checkboxes
    const treeData: TreeViewDataItem[] = useMemo(() => {
        // Names of selected source ROIs that are enabled (isIncluded). Used to warn users when a
        // target output already has an enabled ROI with the same name, which is not recommended (but not
        // disallowed).
        const selectedEnabledSourceNames = new Set(
            advancedRois
                .filter(roi => selectedRoiIds.has(roi.id) && roi.isIncluded)
                .map(roi => roi.name)
        );

        return buildCopyTargetTree({
            contourTreeViewItems, searchText,
            areDeprecatedModelsShown, areNonLicensedModelsShown, areUserHiddenModelsShown,
            sourceOutputId, selectedTargetOutputIds,
            sourceBaseId: sourceBase?.id, sourceModelId, sourceBodyPart,
            selectedEnabledSourceNames, outputEntities, roiEntities, t,
        });
    }, [contourTreeViewItems, searchText, areDeprecatedModelsShown, areNonLicensedModelsShown,
        areUserHiddenModelsShown, selectedTargetOutputIds, sourceOutputId, sourceBase?.id,
        sourceModelId, sourceBodyPart, advancedRois, selectedRoiIds, roiEntities, outputEntities, t]);

    const handleCheck = useCallback(
        (_event: React.ChangeEvent<HTMLInputElement>, item: TreeViewDataItem) => {
            if (!item.id) { return; }
            setSelectedTargetOutputIds(prev => {
                const next = new Set(prev);
                const ids = collectOutputIds(item).filter(id => id !== sourceOutputId);
                const allChecked = ids.every(id => prev.has(id));
                ids.forEach(id => allChecked ? next.delete(id) : next.add(id));
                return next;
            });
        }, [sourceOutputId]
    );

    const handleCopy = useCallback(() => {
        dispatch(advancedRoisCopiedToOutputs({
            sourceRoiIds: Array.from(selectedRoiIds),
            targetOutputIds: Array.from(selectedTargetOutputIds),
        }));
        onClose();
    }, [dispatch, selectedRoiIds, selectedTargetOutputIds, onClose]);

    const isCopyDisabled = selectedRoiIds.size === 0 || selectedTargetOutputIds.size === 0;

    const hasTargets = treeData.length > 0;
    const hasAdvancedRois = advancedRois.length > 0;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            variant={ModalVariant.medium}
            title={t('copyAdvancedRois.modal.title')}
            actions={[
                <Button
                    key="copy"
                    variant="primary"
                    onClick={handleCopy}
                    isDisabled={isCopyDisabled}
                    data-testid="copy-advanced-rois-copy-button"
                >
                    {t('copyAdvancedRois.modal.copy')}
                </Button>,
                <Button
                    key="cancel"
                    variant="link"
                    onClick={onClose}
                    data-testid="copy-advanced-rois-cancel-button"
                >
                    {t('common.cancel')}
                </Button>,
            ]}
            data-testid="copy-advanced-rois-modal"
        >
            <Alert variant="info" isInline title={t('copyAdvancedRois.modal.warning')} />

            <TextContent style={{ marginTop: '1rem' }}>
                <Text component={TextVariants.h3}>{t('copyAdvancedRois.modal.selectStructures')}</Text>
            </TextContent>

            {!hasAdvancedRois && (
                <TextContent><Text>{t('copyAdvancedRois.modal.noAdvancedRois')}</Text></TextContent>
            )}

            {hasAdvancedRois && advancedRois.map(roi => (
                <Checkbox
                    key={roi.id}
                    id={`copy-roi-${roi.id}`}
                    label={`${roi.name} (${roi.operation})`}
                    isChecked={selectedRoiIds.has(roi.id)}
                    onChange={(_event, checked) => handleRoiCheckboxChange(roi.id, checked)}
                    data-testid={`copy-advanced-rois-roi-checkbox-${roi.id}`}
                    style={{ marginLeft: '0.5rem' }}
                />
            ))}

            {/* Search and filter controls */}
            <div style={{ marginTop: '1rem' }}>
                <CustomizationSearchBar value={searchText} onChange={setSearchText} />
                <CustomizationFilters modelType={ModelType.Contouring} />
            </div>

            <TextContent style={{ marginTop: '1rem' }}>
                <Text component={TextVariants.h3}>{t('copyAdvancedRois.modal.selectTargets')}</Text>
            </TextContent>

            {!hasTargets && (
                <TextContent><Text>{t('copyAdvancedRois.modal.noTargets')}</Text></TextContent>
            )}

            {hasTargets && (
                <TreeView
                    data={treeData}
                    hasCheckboxes
                    onCheck={handleCheck}
                    hasGuides
                />
            )}
        </Modal>
    );
};

export default CopyAdvancedRoisModal;
